import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  useDisclosure,
  Center,
  HStack,
  Tooltip,
  IconButton,
  Select,
  useToast,
  Skeleton,
  SkeletonText,
  Box,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Grid, GridItem,
  FormControl,
  FormLabel,
  ModalFooter,
  Text
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobSkillsAsync,
  selectJobSkills,
} from "../../../JobPostingSlice/JobSkillsList/JobSkillsSlice";
import {
  addJobEducationAsync,
  deleteJobEducationAsync,
  fetchJobEducationAsync,
  selectJobEducation,
  updateJobEducationAsync,
} from "../../../JobPostingSlice/JobEducation/JobEducationSlice";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";

function MinmQualif() {
  const dispatch = useDispatch();
  const jobEducation = useSelector(selectJobEducation);
  const toast = useToast();

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchJobEducationAsync());
      } catch (error) {
        toast({
          title: "Error loading data",
          description: "Failed to load education degrees",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, toast]);

  const {
    isOpen: isOpen1,
    onOpen: onOpen1,
    onClose: onClose1,
  } = useDisclosure();
  const {
    isOpen: isOpen2,
    onOpen: onOpen2,
    onClose: onClose2,
  } = useDisclosure();

  const [newJobEducation, setNewJobEducation] = useState("");
  const [newJobStatus, setNewJobStatus] = useState(""); // Add status state
  const [error, setError] = useState(null);
  const [editJobEducation, setEditJobEducation] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editJobStatus, setEditJobStatus] = useState("");
  const [editableJobEducation, setEditableJobEducation] = useState(null);
  const [deletingEducation, setDeletingEducation] = useState(null);

  const handleDelete = async (uuid) => {
    setIsDeleting(true);
    try {
      await dispatch(deleteJobEducationAsync(uuid));
      await dispatch(fetchJobEducationAsync());
      toast({
        title: "Success",
        description: "Education degree deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      onClose2();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete education degree",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (job) => {
    setDeletingEducation(job);
    onOpen2();
  };

  const handleSave = async () => {
    if (!newJobEducation.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a degree title",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsSubmitting(true);
    const jobEducation = {
      title: newJobEducation,
      status: "active", // Get status from input
    };

    try {
      console.log("New Job Component:", jobEducation);
      await dispatch(addJobEducationAsync(jobEducation));
      await dispatch(fetchJobEducationAsync());

      toast({
        title: "Success",
        description: "Education degree added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      setNewJobEducation(""); // Clear input field after saving
      setNewJobStatus(""); // Clear status field
      onClose1();
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to add education degree",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (jobEducation) => {
    setEditableJobEducation(jobEducation);
    setEditJobEducation(jobEducation.title);
    setEditJobStatus(jobEducation.status);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editJobEducation.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a degree title",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsUpdating(true);
    try {
      await dispatch(
        updateJobEducationAsync({
          uuid: editableJobEducation.uuid,
          jobEducationData: {
            title: editJobEducation,
            status: editJobStatus, // Include status
          },
        })
      );

      await dispatch(fetchJobEducationAsync());

      toast({
        title: "Success",
        description: "Education degree updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      setEditableJobEducation(null);
      setEditJobEducation("");
      setEditJobStatus(""); // Reset status
      setIsEditModalOpen(false);
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to update education degree",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const SkeletonRows = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <Tr key={index}>
          <Td>
            <Skeleton height="20px" width="30px" />
          </Td>
          <Td>
            <Skeleton height="20px" width="150px" />
          </Td>
          <Td>
            <Skeleton height="20px" width="60px" />
          </Td>
          <Td>
            <HStack spacing={2}>
              <Skeleton height="32px" width="32px" borderRadius="md" />
              <Skeleton height="32px" width="32px" borderRadius="md" />
            </HStack>
          </Td>
        </Tr>
      ))}
    </>
  );

  return (
    <div
      className="min-h-screen bg-adminbg contentAdmin p-4"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="">
        <div className="w-full bg-white my-5 rounded-md">
          <div className="flex justify-between">
            <Box className="px-3 py-7">
              {isLoading ? (
                <SkeletonText noOfLines={1} skeletonHeight="28px" width="200px" />
              ) : (
                <p className="text-xl text-black font-semibold">
                  Education Degrees
                </p>
              )}
            </Box>
            <div className="py-4 px-3 flex gap-3">
              <Button
                colorScheme="blue"
                fontSize={"medium"}
                alignContent={"center"}
                onClick={onOpen1}
                isDisabled={isLoading}
              >
                Add<i className="fa-solid fa-plus px-2"></i>
              </Button>

              {/* Add Education Degree Modal */}
              <Modal isOpen={isOpen1} onClose={onClose1} size="4xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Add Education Degree
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <form onSubmit={handleSave}>
                    <ModalBody py={6}>
                      <VStack spacing={6}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                          <GridItem colSpan={2}>
                            <FormControl>
                              <FormLabel>Education Degree Title</FormLabel>
                              <Input
                                color="black"
                                placeholder="Enter Degree Title"
                                value={newJobEducation}
                                onChange={(e) => setNewJobEducation(e.target.value)}
                                isDisabled={isSubmitting}
                              />
                            </FormControl>
                          </GridItem>
                          <GridItem colSpan={2}>
                            <FormControl>
                              <FormLabel>Status</FormLabel>
                              <Select
                                placeholder="Select Degree Status"
                                value={newJobStatus}
                                onChange={(e) => setNewJobStatus(e.target.value)}
                                color="black"
                                borderColor="black"
                                isDisabled={isSubmitting}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </Select>
                            </FormControl>
                          </GridItem>
                        </Grid>
                      </VStack>
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                      <Button
                        colorScheme="blue"
                        mr={3}
                        onClick={handleSave}
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                      >
                        Save
                      </Button>
                      <Button onClick={onClose1} isDisabled={isSubmitting}>
                        Cancel
                      </Button>
                    </ModalFooter>
                  </form>
                </ModalContent>
              </Modal>

              {/* Delete Education Degree Modal */}
              <Modal isOpen={isOpen2} onClose={onClose2}>
                <ModalOverlay />
                <ModalContent maxW="2xl" borderRadius="lg" boxShadow="lg">
                  <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                    Confirm Deletion
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text fontSize="md" textAlign="center">
                      Are you sure you want to delete{" "}
                      <Text as="span" fontWeight="semibold" color="red.500">
                        "{deletingEducation?.title}"
                      </Text>
                      ?
                    </Text>
                  </ModalBody>
                  <ModalFooter display="flex" gap="2" justifyContent="center">
                    <Button
                      colorScheme="red"
                      w="40"
                      onClick={() => handleDelete(deletingEducation.uuid)}
                      isLoading={isDeleting}
                      loadingText="Deleting..."
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="red"
                      w="40"
                      onClick={onClose2}
                      isDisabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              {/* Edit Education Degree Modal */}
              <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="4xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Edit Education Degree
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody py={6}>
                    <VStack spacing={6}>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Education Degree Title</FormLabel>
                            <Input
                              color="black"
                              placeholder="Enter Degree Title"
                              value={editJobEducation}
                              onChange={(e) => setEditJobEducation(e.target.value)}
                              isDisabled={isUpdating}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Status</FormLabel>
                            <Select
                              placeholder="Select Degree Status"
                              value={editJobStatus}
                              onChange={(e) => setEditJobStatus(e.target.value)}
                              color="black"
                              borderColor="black"
                              isDisabled={isUpdating}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </Select>
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </VStack>
                  </ModalBody>
                  <ModalFooter bg="gray.50" borderBottomRadius="md">
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={handleUpdate}
                      isLoading={isUpdating}
                      loadingText="Updating..."
                    >
                      Update
                    </Button>
                    <Button
                      onClick={() => setIsEditModalOpen(false)}
                      isDisabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                  {error && (
                    <Text color="red.500" textAlign="center" pb={4}>
                      {error}
                    </Text>
                  )}
                </ModalContent>
              </Modal>
            </div>
          </div>
          <TableContainer>
            <Table size="sm">
              <Thead className="bg-gray-200">
                <Tr>
                  <Th className="w-1/4">ID</Th>
                  <Th className="w-1/4">Title</Th>
                  <Th className="w-1/4">Status</Th>
                  <Th className="w-1/4">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <SkeletonRows />
                ) : jobEducation.length > 0 ? (
                  [...jobEducation] // Creating a copy to avoid mutating state
                    .sort(
                      (a, b) => new Date(a.created_at) - new Date(b.created_at)
                    ) // Sorting in ascending order (earliest first, latest last)
                    .map((job, index) => (
                      <Tr key={job.id}>
                        <Td>{index + 1}</Td> {/* Displaying index-based ID */}
                        <Td>{job.title}</Td>
                        <Td textColor={"green"}>{job.status}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Edit Education Degrees" placement="top">
                              <IconButton
                                icon={<EditIcon />}
                                colorScheme="blue"
                                size="sm"
                                onClick={() => handleEdit(job)}
                                aria-label="Edit industry"
                              />
                            </Tooltip>
                            <Tooltip label="Delete Education Degrees" placement="top">
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                size="sm"
                                onClick={() => openDeleteModal(job)}
                                aria-label="Delete industry"
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                ) : (
                  <Tr>
                    <Td colSpan="4" className="text-center py-4 text-gray-500">
                      No data available
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

export default MinmQualif;